import React, { useState, useEffect, useId, useRef } from 'react';

// MUI Components
import { TextField, Autocomplete, Divider, Typography, Box, Checkbox, Chip } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';

// Icons
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBoxOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOffOutlined';
import { CrossIconCIcon, SelectCloseCIcon } from '~/assets/images';
import OlcDropdownChevron from '~/components/General/OlcDropdownChevron';
import OlcTooltip from '~/components/General/OlcTooltip';

// styles
import './styles.scss';

const GenericAutocomplete = (props) => {
  const {
    placeholder,
    options,
    selectedValue,
    setSelectedValue,
    search,
    updateErrors,
    name,
    loading,
    disable = false,
    multiple = false,
    disableClearable = true,
    templateBuilder = false,
    filterAutoComplete = false,
    error = false,
    tagsClass = false,
    campaign = false,
    className = '',
    newDesign = false,
    listClass = '',
    dropdownType = '',
    persistSearch = false,
    searchValue = '',
    readOnlyInput = false,
    hideClearIcon = false,
    hideDividers = false,
    nativeScrollbar = false,
    menuClassName = '',
    showSelectedTooltip = false,
  } = props;
  const [menuOpen, setMenuOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const lastInputValueRef = useRef('');
  const autocompleteId = useId();
  const normalizedSelectedValue = multiple
    ? selectedValue || []
    : Array.isArray(selectedValue)
      ? null
      : selectedValue;
  const hasSelectedValue = Array.isArray(normalizedSelectedValue)
    ? normalizedSelectedValue.length > 0
    : Boolean(normalizedSelectedValue);

  // Sync inputValue with searchValue when persistSearch is enabled
  useEffect(() => {
    if (persistSearch) {
      // Only sync if searchValue is different from what we have (user typed something new)
      // This prevents resetting when options are selected
      if (searchValue !== lastInputValueRef.current) {
        setInputValue(searchValue || '');
        lastInputValueRef.current = searchValue || '';
      }
    }
  }, [searchValue, persistSearch]);

  const updateValue = (event, newValue) => {
    event.preventDefault();
    !newValue?.func ? setSelectedValue(newValue) : undefined;
    updateErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
    newValue?.func ? newValue?.func() : undefined;
    if (!persistSearch) {
      search('');
    }
  };

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = (
    <CheckBoxIcon
      fontSize="small"
      sx={{
        color: '#ED5C2F',
      }}
    />
  );

  const clearIcon = tagsClass ? null : hasSelectedValue ? (
    // NOTE:
    // Do NOT attach onClick handlers directly to this element.
    // MUI wraps `clearIcon` with an IconButton which already handles clearing the value.
    // Adding an onClick here causes the "child of a button element" prop-type warning.
    <img
      src={newDesign ? SelectCloseCIcon : CrossIconCIcon}
      style={{
        top: '5px',
        width: newDesign ? '11px' : 'inherit',
        height: newDesign ? '11px' : 'inherit',
      }}
      alt="clear"
    />
  ) : null;

  const getAutocompleteOptionLabel = (option) => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    const label = option.label ?? option.title ?? '';
    return typeof label === 'string' ? label : String(label);
  };

  const isAutocompleteOptionEqual = (option, value) => {
    if (!option || !value) return option === value;
    if (
      typeof option !== 'string' &&
      typeof value !== 'string' &&
      option.id != null &&
      value.id != null
    ) {
      return String(option.id) === String(value.id);
    }

    return getAutocompleteOptionLabel(option) === getAutocompleteOptionLabel(value);
  };

  const isOptionSelected = (option, selected) => {
    if (selected) return true;
    if (!Array.isArray(normalizedSelectedValue)) return false;
    return normalizedSelectedValue.some((value) => isAutocompleteOptionEqual(option, value));
  };

  const selectedTooltipText = showSelectedTooltip
    ? Array.isArray(normalizedSelectedValue)
      ? normalizedSelectedValue.map(getAutocompleteOptionLabel).filter(Boolean).join(', ')
      : getAutocompleteOptionLabel(normalizedSelectedValue)
    : '';

  const renderOption = (props, option, { selected }) => {
    const optionLabel = getAutocompleteOptionLabel(option);
    const optionKey = typeof option === 'string' ? option : (option.id ?? optionLabel);

    return (
      <div key={optionKey}>
        {loading && options.indexOf(option) === 0 ? (
          <Box>
            <LinearProgress
              sx={{
                backgroundColor: 'white',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#ED5C2F',
                },
              }}
            />
          </Box>
        ) : null}
        {dropdownType === 'campaign' ? (
          <>
            {option.custom ? (
              <>
                {options?.some((opt) => !opt.custom) && !option.hideDividerAbove && (
                  <Divider sx={{ my: 1, borderColor: 'rgba(0, 0, 0, 0.12)', width: '100%' }} />
                )}
                <li {...props} key={optionKey}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: '5px',
                    }}
                  >
                    {option.icon && <img src={option.icon} alt="icon" />}
                    <Typography
                      className="campaignDropdownNewBtn"
                      sx={{ color: option.custom ? '#ED5C2F' : '' }}
                    >
                      {optionLabel}
                    </Typography>
                  </Box>
                </li>
              </>
            ) : (
              <li {...props} key={optionKey}>
                {option.custom ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: '5px',
                    }}
                  >
                    {option.icon && <img src={option.icon} alt="icon" />}
                    <Typography sx={{ color: option.custom ? '#ED5C2F' : '' }}>
                      {optionLabel}
                    </Typography>
                  </Box>
                ) : multiple ? (
                  <>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={isOptionSelected(option, selected)}
                    />
                    {optionLabel}
                  </>
                ) : (
                  <Typography
                    className={`${templateBuilder ? 'tempBuilderOptions' : ''} ${listClass || ''}`}
                  >
                    {optionLabel}
                  </Typography>
                )}
              </li>
            )}
          </>
        ) : (
          <>
            {option.custom && !hideDividers && !option.hideDividerAbove ? (
              <Divider className="listDivider" sx={{ margin: '8px 0px !important' }} />
            ) : null}
            <li {...props} key={optionKey}>
              {option.custom ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  {option.icon && <img src={option.icon} alt="icon" />}
                  <Typography
                    sx={{
                      color: option.custom ? '#ED5C2F !important' : '',
                      fontWeight: option.custom ? '400 !important' : '',
                    }}
                  >
                    {optionLabel}
                  </Typography>
                </Box>
              ) : multiple ? (
                <>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={isOptionSelected(option, selected)}
                  />
                  {optionLabel}
                </>
              ) : (
                <Typography className={templateBuilder && 'tempBuilderOptions'}>
                  {optionLabel}
                </Typography>
              )}
            </li>
          </>
        )}
        {className !== 'newAutocompleteDesign' && !hideDividers && (
          <Divider className="listDivider" />
        )}
      </div>
    );
  };

  const getAutocompleteClassName = (name) => {
    const componentNames = ['template'];
    const designClassName = componentNames.includes(name)
      ? 'autocompletePaper'
      : newDesign
        ? 'newAutocompleteMenuDesignPrimary'
        : '';

    return [designClassName, menuClassName].filter(Boolean).join(' ');
  };

  return (
    <Autocomplete
      className={
        multiple
          ? `multipleAutoSelect ${tagsClass && 'tagsUpdateSelect'}`
          : templateBuilder
            ? 'tempAutocomplete'
            : `genericAutocomplete ${filterAutoComplete ? 'filterAutoComplete' : ''} ${error ? 'errorAutocomplete' : ''} ${className}`
      }
      multiple={multiple}
      renderTags={(value, getTagProps) =>
        tagsClass
          ? null
          : value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={
                  typeof option === 'string'
                    ? option
                    : (option.id ?? getAutocompleteOptionLabel(option))
                }
                label={getAutocompleteOptionLabel(option)}
                deleteIcon={<HighlightOffIcon sx={{ color: '#ED5C2F' }} />}
              />
            ))
      }
      clearIcon={hideClearIcon ? null : clearIcon}
      sx={{
        maxWidth: campaign ? '100%' : '564px',
        background: campaign ? 'white' : 'transparent',
        '& .MuiAutocomplete-clearIndicator': {
          visibility: hasSelectedValue ? 'visible' : 'hidden',
        },
      }}
      limitTags={multiple && tagsClass ? 3 : multiple ? 1 : undefined}
      disabled={disable}
      disableCloseOnSelect={multiple}
      disableClearable={disableClearable || hideClearIcon}
      id={autocompleteId}
      isOptionEqualToValue={isAutocompleteOptionEqual}
      getOptionLabel={getAutocompleteOptionLabel}
      value={normalizedSelectedValue}
      loading={loading}
      inputValue={persistSearch ? inputValue : undefined}
      onInputChange={(event, newInputValue, reason) => {
        if (readOnlyInput) return;

        if (persistSearch) {
          if (reason === 'input') {
            setInputValue(newInputValue);
            lastInputValueRef.current = newInputValue;
            search(newInputValue);
          } else if (reason === 'reset') {
            setInputValue(lastInputValueRef.current);
          }
        } else if (reason === 'input' || reason === 'clear') {
          search(newInputValue || '');
        }
      }}
      onChange={(event, newValue) => {
        updateValue(event, newValue);
        if (!persistSearch && !multiple) {
          search(getAutocompleteOptionLabel(newValue));
        }
      }}
      onKeyDown={(event) => {
        if (readOnlyInput) {
          // Prevent all keyboard input except Escape, Arrow keys, Tab, and function keys for navigation
          if (
            event.key !== 'Escape' &&
            !event.key.startsWith('Arrow') &&
            !event.key.startsWith('F') &&
            event.key !== 'Tab' &&
            event.key !== 'Enter'
          ) {
            event.preventDefault();
          }
        }
      }}
      onBlur={(event) => {
        if (!persistSearch && !readOnlyInput) {
          search(event.target.value || '');
        }
      }}
      onFocus={(event) => {
        if (!persistSearch && !readOnlyInput) {
          search(event.target.value || '');
        }
        if (event?.target?.setSelectionRange) {
          requestAnimationFrame(() => {
            const cursorPosition = event.target.value?.length || 0;
            event.target.setSelectionRange(cursorPosition, cursorPosition);
          });
        }
      }}
      options={options}
      onOpen={() => setMenuOpen(true)}
      onClose={() => setMenuOpen(false)}
      popupIcon={<OlcDropdownChevron disabled={disable} open={menuOpen} />}
      renderInput={(params) => {
        const inputField = (
          <TextField
            {...params}
            placeholder={
              hasSelectedValue && tagsClass ? placeholder : hasSelectedValue ? '' : placeholder
            }
            size="small"
            className={templateBuilder ? 'tempAutoField' : ''}
            inputProps={{
              ...params.inputProps,
              'aria-label': placeholder || name || 'Select option',
              readOnly: readOnlyInput,
              onFocus: (e) => {
                params.inputProps?.onFocus?.(e);
                if (readOnlyInput && hasSelectedValue) {
                  // Clear any text selection on focus - use requestAnimationFrame to not interfere with dropdown
                  requestAnimationFrame(() => {
                    e.target.setSelectionRange(0, 0);
                  });
                } else {
                  requestAnimationFrame(() => {
                    const cursorPosition = e.target.value?.length || 0;
                    e.target.setSelectionRange(cursorPosition, cursorPosition);
                  });
                }
              },
              onSelect: (e) => {
                params.inputProps?.onSelect?.(e);
                if (readOnlyInput && hasSelectedValue) {
                  // Clear selection if it happens
                  requestAnimationFrame(() => {
                    e.target.setSelectionRange(0, 0);
                  });
                } else {
                  requestAnimationFrame(() => {
                    const cursorPosition = e.target.value?.length || 0;
                    e.target.setSelectionRange(cursorPosition, cursorPosition);
                  });
                }
              },
            }}
            sx={
              readOnlyInput
                ? {
                    '& input': {
                      cursor: 'pointer !important',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                      caretColor: 'transparent',
                      '&::selection': {
                        background: 'transparent',
                      },
                      '&::-moz-selection': {
                        background: 'transparent',
                      },
                    },
                  }
                : undefined
            }
          />
        );

        return selectedTooltipText ? (
          <OlcTooltip title={selectedTooltipText} placement="bottom-start">
            {inputField}
          </OlcTooltip>
        ) : (
          inputField
        );
      }}
      renderOption={renderOption}
      classes={{
        paper: [getAutocompleteClassName(name), tagsClass ? 'tagsPaper' : '']
          .filter(Boolean)
          .join(' '),
      }}
      getOptionDisabled={(option) => option.customPlaceholder}
      ListboxProps={{
        className: ['olcSingleSelectListbox', nativeScrollbar ? 'olcNativeScrollbar' : '']
          .filter(Boolean)
          .join(' '),
        style: { maxHeight: 250 },
      }}
    />
  );
};

export default GenericAutocomplete;
